import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { EonUiPagination, EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useLanguage } from "@me8eon/language";
import { EnvironmentConfig } from "@me8eon/config";
import { OnboardingAppData } from "../OnboardingPortal.types";
import OnboardingPortalAppIcon from "../components/OnboardingPortalAppIcon";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalOwnerFilter from "../components/OnboardingPortalOwnerFilter";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalExportButton from "../components/OnboardingPortalExportButton";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import OnboardingPortalCompanyFilter from "../components/OnboardingPortalCompanyFilter";
import {
    exportAppsDataAsCsv,
    exportAppsDataAsExcel,
} from "../OnboardingPortal.export.utils";
import { getCompanyCanonicalId, getCompanyNameFromId } from "../OnboardingPortal.utils";

const ReviewDefaultAppActivityScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        handleReviewDefaultAppActivity,
        reviewDefaultAppActivity,
        fetchReviewDefaultAppActivity,
        isLoadingReviewDefaultAppActivity,
        isSubmittingReviewDefaultAppActivity,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [owner, setOwner] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("pending");
    const [company, setCompany] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredApps = useMemo(() => {
        const selectedCompanyId = getCompanyCanonicalId(company);
        return reviewDefaultAppActivity.filter((app) => {
            const matchesSearch =
                search.trim() === ""
                    ? true
                    : app?.app?.title
                          .toLowerCase()
                          .includes(search.toLowerCase()) ||
                      app?.app?.subTitle
                          .toLowerCase()
                          .includes(search.toLowerCase());
            const matchesStatus =
                status === "all" ? true : app.status === status;
            const matchesOwner = owner === "" ? true : app.owner === owner;
            const matchesCompany =
                selectedCompanyId === ""
                    ? true
                    : getCompanyCanonicalId(app.company) === selectedCompanyId;
            return (
                matchesSearch && matchesStatus && matchesOwner && matchesCompany
            );
        });
    }, [search, status, owner, company, reviewDefaultAppActivity]);

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredApps.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredApps]);

    useEffect(() => {
        if (showModule === "review-apps") {
            fetchReviewDefaultAppActivity(jwt);
        }
    }, [showModule]);

    const fetchExportApps = async (): Promise<OnboardingAppData[]> => {
        const API_BASE_URL = EnvironmentConfig.nodeService;
        const API_ENDPOINT_APPS = `${API_BASE_URL}/service/onboarding/apps?view=export`;
        const token = await jwt();
        const response = await fetch(API_ENDPOINT_APPS, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            const errorBody = await response.json();
            throw new Error(
                `Failed to get data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
            );
        }
        const data: { isSuccess: boolean; apps: OnboardingAppData[] } =
            await response.json();
        if (data.isSuccess !== true) {
            console.warn(
                "API call succeeded, but response not marked as success."
            );
        }
        if (
            !data.apps ||
            !Array.isArray(data.apps) ||
            data.apps.length === 0
        ) {
            throw new Error(
                "API response does not contain a valid 'apps' array."
            );
        }
        return data.apps;
    };

    // Most browsers (e.g. Chrome) block/prompt when several downloads are
    // triggered back-to-back from the same user gesture. Staggering the
    // downloads with a small delay avoids that restriction.
    const triggerDownloadsSequentially = (
        downloads: Array<() => void>,
        delayMs = 300
    ) => {
        downloads.forEach((download, index) => {
            setTimeout(download, index * delayMs);
        });
    };

    const exportApps = async (format: "csv" | "excel") => {
        try {
            const apps = await fetchExportApps();
            if (format === "csv") {
                exportAppsDataAsCsv(apps, "apps");
            } else if (format === "excel") {
                exportAppsDataAsExcel(apps, "apps");
            }
        } catch (error) {
            console.error("Error in fetchReviewDefaultAppActivity:", error);
            throw error;
        }
    };

    const exportAppsBothFormats = async (fileName: string) => {
        try {
            const apps = await fetchExportApps();
            triggerDownloadsSequentially([
                () => exportAppsDataAsCsv(apps, fileName),
                () => exportAppsDataAsExcel(apps, fileName),
            ]);
        } catch (error) {
            console.error("Error in fetchReviewDefaultAppActivity:", error);
            throw error;
        }
    };

    return (
        <>
            <div className="onboarding-portal--content--card">
                {(isLoadingReviewDefaultAppActivity ||
                    isSubmittingReviewDefaultAppActivity) && (
                    <OnboardingPortalLoader />
                )}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.defaultApps")} (
                        {filteredApps.length})
                    </div>
                    <div className="onboarding-portal--content--card--header--actions">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchReviewDefaultAppActivity(jwt)}
                        />
                        {status === "pending" && filteredApps.length > 0 && (
                            <EonUiButton
                                size="small"
                                onClick={() => {
                                    handleReviewDefaultAppActivity(
                                        jwt,
                                        filteredApps.map((app) => ({
                                            entityId: app.id!,
                                            reviewerAction: "approved",
                                        }))
                                    );
                                }}
                                rank="secondary"
                                text="Approve All"
                                style={{ flexShrink: 0 }}
                            />
                        )}
                        {status === "approved" && filteredApps.length > 0 && (
                            <EonUiButton
                                size="small"
                                onClick={() => {
                                    handleReviewDefaultAppActivity(
                                        jwt,
                                        filteredApps.map((app) => ({
                                            entityId: app.id!,
                                            reviewerAction: "inQA",
                                        }))
                                    );
                                }}
                                rank="secondary"
                                text="Move all to QA"
                                style={{ flexShrink: 0 }}
                            />
                        )}
                        {status === "inQA" && filteredApps.length > 0 && (
                            <EonUiButton
                                size="small"
                                onClick={() => {
                                    handleReviewDefaultAppActivity(
                                        jwt,
                                        filteredApps.map((app) => ({
                                            entityId: app.id!,
                                            reviewerAction: "inProd",
                                        }))
                                    );
                                }}
                                rank="secondary"
                                text="Move all to Prod"
                                style={{ flexShrink: 0 }}
                            />
                        )}
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                        />
                        <OnboardingPortalCompanyFilter
                            t={t}
                            company={company}
                            setCompany={setCompany}
                            list={reviewDefaultAppActivity}
                        />
                        <OnboardingPortalStatusFilter
                            t={t}
                            status={status}
                            setStatus={setStatus}
                        />
                        <OnboardingPortalOwnerFilter
                            t={t}
                            owner={owner}
                            setOwner={setOwner}
                            list={filteredApps}
                        />
                        <OnboardingPortalExportButton
                            t={t}
                            onExportCSV={() => exportApps("csv")}
                            onExportExcel={() => exportApps("excel")}
                            moreOptions={[
                                {
                                    text: "Export Dev",
                                    onClick: () =>
                                        exportAppsBothFormats("apps_dev"),
                                },
                                {
                                    text: "Export Prod",
                                    onClick: () =>
                                        exportAppsBothFormats("apps"),
                                },
                            ]}
                        />
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.icon",
                            "onboardingPortal.tableHeaders.title",
                            "onboardingPortal.tableHeaders.company",
                            "onboardingPortal.tableHeaders.update",
                            "onboardingPortal.tableHeaders.status",
                            "onboardingPortal.tableHeaders.owner",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredApps
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((app) => (
                                <tr key={app.id}>
                                    <td>
                                        <OnboardingPortalAppIcon
                                            icon={app?.app?.icon}
                                            iconUrl={app?.app?.iconDataUrl}
                                        />
                                    </td>
                                    <td>{app?.app?.title}</td>
                                    <td>
                                        {getCompanyNameFromId(
                                            app?.company || ""
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={`activity--${app.activity}`}
                                        >
                                            {t(
                                                `onboardingPortal.activityLabel.${app.activity}`
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className={`status--${app.status}`}
                                        >
                                            {t(
                                                `onboardingPortal.statusLabel.${app.status}`
                                            )}
                                        </span>
                                    </td>
                                    <td>{app.owner}</td>
                                    <td>
                                        <div className="actions-row">
                                            {app.status === "pending" && (
                                                <>
                                                    <span
                                                        className="action--approve"
                                                        onClick={() => {
                                                            handleReviewDefaultAppActivity(
                                                                jwt,
                                                                [
                                                                    {
                                                                        entityId:
                                                                            app.id!,
                                                                        reviewerAction:
                                                                            "approved",
                                                                    },
                                                                ]
                                                            );
                                                        }}
                                                    >
                                                        {t(
                                                            "onboardingPortal.approveBtn"
                                                        )}
                                                    </span>
                                                    <span
                                                        className="action--reject"
                                                        onClick={() => {
                                                            handleReviewDefaultAppActivity(
                                                                jwt,
                                                                [
                                                                    {
                                                                        entityId:
                                                                            app.id!,
                                                                        reviewerAction:
                                                                            "rejected",
                                                                    },
                                                                ]
                                                            );
                                                        }}
                                                    >
                                                        {t(
                                                            "onboardingPortal.rejectBtn"
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                            {app.status === "approved" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewDefaultAppActivity(
                                                            jwt,
                                                            [
                                                                {
                                                                    entityId:
                                                                        app.id!,
                                                                    reviewerAction:
                                                                        "inQA",
                                                                },
                                                            ]
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toQABtn"
                                                    )}
                                                </span>
                                            )}
                                            {app.status === "inQA" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewDefaultAppActivity(
                                                            jwt,
                                                            [
                                                                {
                                                                    entityId:
                                                                        app.id!,
                                                                    reviewerAction:
                                                                        "inProd",
                                                                },
                                                            ]
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toProdBtn"
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredApps.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredApps.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredApps.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                    console.log(e.detail);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredApps.length / pageSize)
                        )
                    );
                }}
            />
        </>
    );
};

export default ReviewDefaultAppActivityScreen;
